/**
 * Heartbeat / liveness detection for the WebSocket (ARCHITECTURE §7 — `WsClient`
 * "heartbeat"). Sends periodic pings and declares the connection dead if a pong
 * (or any inbound frame) is not seen within a timeout, so a half-open socket is
 * detected and forces a reconnect.
 *
 * Real, working timer implementation. Environment-agnostic (uses `setInterval`/
 * `setTimeout`, available in both browser and Node/WebView contexts).
 */

export interface HeartbeatOptions {
  /** Interval between pings in ms. Default 25_000. */
  intervalMs?: number;
  /** How long to wait for a pong before declaring death. Default 10_000. */
  timeoutMs?: number;
  /** Called on each tick — the client should send a ping control frame. */
  onPing: () => void;
  /** Called when no pong/activity arrived within the timeout. */
  onTimeout: () => void;
}

type TimerHandle = ReturnType<typeof setInterval>;

/**
 * Drives ping cadence and timeout detection. The owning {@link WsClient} calls
 * {@link start} on open, {@link notifyActivity} on any inbound frame (pong or
 * data), and {@link stop} on close.
 */
export class Heartbeat {
  private readonly intervalMs: number;
  private readonly timeoutMs: number;
  private readonly onPing: () => void;
  private readonly onTimeout: () => void;

  private intervalTimer: TimerHandle | null = null;
  private timeoutTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(options: HeartbeatOptions) {
    this.intervalMs = options.intervalMs ?? 25_000;
    this.timeoutMs = options.timeoutMs ?? 10_000;
    this.onPing = options.onPing;
    this.onTimeout = options.onTimeout;
  }

  /** Begin the ping cadence. Idempotent restart. */
  start(): void {
    this.stop();
    this.intervalTimer = setInterval(() => {
      this.onPing();
      this.armTimeout();
    }, this.intervalMs);
  }

  /** Any inbound frame resets the death timer (a pong counts as activity). */
  notifyActivity(): void {
    if (this.timeoutTimer) {
      clearTimeout(this.timeoutTimer);
      this.timeoutTimer = null;
    }
  }

  /** Stop all timers (on socket close). */
  stop(): void {
    if (this.intervalTimer) {
      clearInterval(this.intervalTimer);
      this.intervalTimer = null;
    }
    if (this.timeoutTimer) {
      clearTimeout(this.timeoutTimer);
      this.timeoutTimer = null;
    }
  }

  /** Arm the pong-wait timer after sending a ping. */
  private armTimeout(): void {
    if (this.timeoutTimer) clearTimeout(this.timeoutTimer);
    this.timeoutTimer = setTimeout(() => {
      this.timeoutTimer = null;
      this.onTimeout();
    }, this.timeoutMs);
  }
}
